import { useEffect, useState } from "react";

import { AuthProvider } from "@descope/react-sdk";

import Error from "./components/Error";
import AuthFlow from "./components/AuthFlow";
import Navbar from "./components/Navbar";
import Introduction from "./components/Introduction";
import BottomNav from "./components/BottomNav";
import SDKShow from "./components/SDKs";
import FlowDownload from "./components/FlowDownload";
import NavbarModal from "./components/NavbarModal";
import { DEFAULT_BASE_URL, sanitizeBaseUrl } from "./util/baseUrl";

import "./App.css";

const defaultTheme = "light";
const defaultStyle = "theme";

const defaultBaseUrl = DEFAULT_BASE_URL;
const STORAGE_KEY = "descope-explorer.settings";

const normalizeOptionalString = (val) => {
  if (val === undefined || val === null) return null;
  const asString = String(val).trim();
  return asString === "" ? null : asString;
};

const getExplorerShareURL = (project, flow, postAuthFlow, style) => {
  const url = new URL(window.location.href);
  const searchParams = url.searchParams;

  searchParams.set("project", project);
  searchParams.set("flow", flow);

  if (postAuthFlow) {
    searchParams.set("postAuthFlow", postAuthFlow);
  } else {
    searchParams.delete("postAuthFlow");
  }

  if (style) {
    searchParams.set("style", style);
  } else {
    searchParams.delete("style");
  }

  url.search = searchParams.toString();
  return url.toString();
};

const setURL = (theme) => {
  // Keep query-string usage scoped to `theme`; project/flow/etc are persisted in localStorage.
  const url = new URL(window.location.href);
  const searchParams = url.searchParams;

  if (theme && theme !== "") {
    searchParams.set("theme", theme);
  } else {
    searchParams.delete("theme");
  }

  url.search = searchParams.toString();
  window.location.replace(url.toString());
};

function App() {
  const defaultProjectId = process.env.REACT_APP_DESCOPE_PROJECT_ID;
  const queryParameters = new URLSearchParams(window.location.search);

  const theme = queryParameters.get("theme") || defaultTheme;
  const bgColorHex = queryParameters.get("bgcolorhex");
  const validateOnBlur = queryParameters.get("validateOnBlur") || false;

  const storedSettings = (() => {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === "object" ? parsed : {};
    } catch {
      return {};
    }
  })();

  const defaultProjectFlow = "sign-up-or-in";

  const [settings, setSettings] = useState(() => {
    const projectId =
      queryParameters.get("project") ||
      normalizeOptionalString(storedSettings.projectId) ||
      defaultProjectId;

    const flowId =
      queryParameters.get("flow") ||
      normalizeOptionalString(storedSettings.flowId) ||
      defaultProjectFlow;

    const postAuthFlowId =
      normalizeOptionalString(queryParameters.get("postAuthFlow")) ??
      normalizeOptionalString(storedSettings.postAuthFlowId);

    const styleId =
      queryParameters.get("style") ||
      normalizeOptionalString(storedSettings.styleId) ||
      defaultStyle;

    // baseUrl controls which origin @descope/web-component fetches
    // config.json/theme.json/screen HTML from, so every candidate - including
    // whatever was previously persisted to localStorage - must be validated
    // against the Descope-operated allowlist, not just trimmed/null-checked.
    // A non-allowlisted value (e.g. from a crafted ?baseUrl= link) is
    // rejected and silently falls back to the default.
    const baseUrl =
      sanitizeBaseUrl(queryParameters.get("baseUrl")) ||
      sanitizeBaseUrl(queryParameters.get("baseURL")) ||
      sanitizeBaseUrl(storedSettings.baseUrl) ||
      defaultBaseUrl;

    return {
      projectId,
      flowId,
      postAuthFlowId,
      styleId,
      baseUrl,
    };
  });

  const {
    projectId: project,
    flowId: flow,
    postAuthFlowId: postAuthFlow,
    styleId: style,
    baseUrl,
  } = settings;
  const adminProject = project === defaultProjectId;

  const [error, setError] = useState(!(theme === "dark" || theme === "light"));
  const [openModal, setOpenModal] = useState({ open: false, modalType: "" });
  const [step, setStep] = useState(0);
  const [downloadFlow, setDownloadFlow] = useState(null);
  const [isCopied, setIsCopied] = useState(false);
  const [flowError, setFlowError] = useState(false);

  useEffect(() => {
    // Persist computed initial settings so first-load values coming from URL/env
    // become sticky for subsequent visits.
    try {
      window.localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          projectId: project,
          flowId: flow,
          postAuthFlowId: postAuthFlow ?? "",
          styleId: style,
          baseUrl: baseUrl,
        }),
      );
    } catch {
      // Ignore storage failures (private mode, disabled storage, etc).
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateSettings = (partial) => {
    const next = {
      ...settings,
      ...partial,
    };

    next.postAuthFlowId = normalizeOptionalString(next.postAuthFlowId);
    next.baseUrl = sanitizeBaseUrl(next.baseUrl) || defaultBaseUrl;

    const shouldReload =
      settings.projectId !== next.projectId ||
      settings.baseUrl !== next.baseUrl;

    try {
      window.localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          projectId: next.projectId,
          flowId: next.flowId,
          postAuthFlowId: next.postAuthFlowId ?? "",
          styleId: next.styleId,
          baseUrl: next.baseUrl,
        }),
      );

      // Remove project/flow/etc query params so localStorage is the source of truth.
      const url = new URL(window.location.href);
      [
        "project",
        "flow",
        "postAuthFlow",
        "style",
        "baseUrl",
        "baseURL",
      ].forEach((k) => url.searchParams.delete(k));
      window.history.replaceState({}, "", url.toString());
    } catch {
      // Ignore storage failures.
    }

    setSettings(next);
    setStep(0);
    setError(false);
    setFlowError(false);
    setDownloadFlow(null);

    if (shouldReload) {
      window.location.reload();
    }
  };

  const resetSettings = () => {
    updateSettings({
      projectId: defaultProjectId,
      flowId: defaultProjectFlow,
      postAuthFlowId: null,
      styleId: defaultStyle,
      baseUrl: defaultBaseUrl,
    });
  };

  const copyExplorerLink = async () => {
    const shareURL = getExplorerShareURL(project, flow, postAuthFlow, style);

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareURL);
      } else {
        const textArea = document.createElement("textarea");
        textArea.value = shareURL;
        textArea.style.position = "fixed";
        textArea.style.left = "-9999px";
        document.body.appendChild(textArea);
        textArea.select();
        try {
          textArea.select();
          document.execCommand("copy");
        } finally {
          document.body.removeChild(textArea);
        }
      }
      setIsCopied(true);
      window.setTimeout(() => setIsCopied(false), 2000);
    } catch (err) {
      console.log("Unable to copy link", err);
    }
  };

  if (theme === "dark") {
    document.documentElement.setAttribute("data-theme", "dark");
  }

  return (
    <>
      {project && flow && !error ? (
        <>
          <NavbarModal
            openModal={openModal}
            defaultFlow={flow}
            defaultProjectId={project}
            setOpenModal={setOpenModal}
            postAuthFlow={postAuthFlow}
            styleId={style}
            baseUrl={baseUrl}
            theme={theme}
            onUpdateSettings={updateSettings}
            onResetSettings={resetSettings}
          />
          {flowError ? (
            <Error
              onOpenSettings={() => setOpenModal({ open: true, modalType: "Settings" })}
            />
          ) : (
            <>
              <Navbar theme={theme} setURL={setURL} setOpenModal={setOpenModal} />
              <Introduction theme={theme} />
              {adminProject && (
                <FlowDownload
                  defaultFlow={flow}
                  theme={theme}
                  step={step}
                  projectId={project}
                  postAuthFlow={postAuthFlow}
                  styleId={style}
                  downloadFlow={downloadFlow}
                  setDownloadFlow={setDownloadFlow}
                  onFlowIdChange={(nextFlowId) =>
                    updateSettings({ flowId: nextFlowId })
                  }
                />
              )}
              {!adminProject && project && flow && (
                <div className="page share-link-wrapper">
                  <button className="share-link-btn" onClick={copyExplorerLink}>
                    {isCopied ? "Link copied" : "Copy link"}
                  </button>
                </div>
              )}
              <AuthProvider
                key={`${project}|${baseUrl}`}
                projectId={project}
                baseUrl={baseUrl}
                baseStaticUrl={`${baseUrl}/pages`}
              >
                <AuthFlow
                  defaultFlow={flow}
                  theme={theme}
                  borderRounded={adminProject}
                  projectId={project}
                  flowId={flow}
                  baseUrl={baseUrl}
                  step={step}
                  setStep={setStep}
                  downloadFlow={downloadFlow}
                  postAuthFlow={postAuthFlow}
                  styleId={style}
                  bgColorHex={bgColorHex}
                  validateOnBlur={validateOnBlur}
                  onResetSettings={resetSettings}
                  onUpdateSettings={updateSettings}
                  onFlowError={() => setFlowError(true)}
                />
              </AuthProvider>
              {project !== defaultProjectId && (
                <div style={{ paddingBottom: "50px" }} />
              )}
              {project === defaultProjectId && <SDKShow theme={theme} />}
              {project === defaultProjectId && <BottomNav />}
            </>
          )}
        </>
      ) : (
        <Error
          onOpenSettings={() => setOpenModal({ open: true, modalType: "Settings" })}
        />
      )}
    </>
  );
}

export default App;
